import { useMemo, useState } from 'react';
import { CATEGORY_COLORS, categoryColor, type SkillDef } from '@/mocks/pipeline';
import SkillPreviewDrawer from './SkillPreviewDrawer';
import { paletteGroups, groupStartsOpen, matchesPaletteQuery, ioCardLabel } from '../skillPaletteGroups';

const TOOLS = [
  { type: 'sticky', name: 'Sticky Note', icon: 'ri-sticky-note-line', wrap: 'bg-warningSoft text-warning', desc: 'A free-form note on the canvas' },
  { type: 'process', name: 'Process', icon: 'ri-git-merge-line', wrap: 'bg-accentSoft text-accent', desc: 'Wraps a group of skills' },
  { type: 'loop', name: 'Loop', icon: 'ri-loop-left-line', wrap: 'bg-success/10 text-success', desc: 'Repeats a single skill N times' },
  { type: 'scheduler', name: 'Scheduler', icon: 'ri-time-line', wrap: 'bg-accentSoft text-accent', desc: 'Polls on a cadence, then runs connected skills' },
];

type Props = {
  skills: SkillDef[];
  previewSkill: SkillDef | null;
  onPreviewSkill: (skillId: string | null) => void;
  onDragStart: (s: SkillDef) => void;
  onResolve: (description: string) => Promise<void>;
};

export default function SkillsPalette({ skills, previewSkill, onPreviewSkill, onDragStart, onResolve }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [describe, setDescribe] = useState('');
  const [resolving, setResolving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const groups = useMemo(() => paletteGroups(skills), [skills]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return skills.filter((s) => matchesPaletteQuery(s, q));
  }, [skills, query]);

  const groupedFiltered = useMemo(() => {
    const groupsMap = new Map<string, SkillDef[]>();
    for (const s of filtered) {
      const disc = s.discipline || 'Uncategorized';
      if (!groupsMap.has(disc)) {
        groupsMap.set(disc, []);
      }
      groupsMap.get(disc)!.push(s);
    }
    return Array.from(groupsMap.entries()).map(([discipline, skills]) => ({ discipline, skills }));
  }, [filtered]);

  const submitDescribe = async () => {
    const d = describe.trim();
    if (!d || resolving) return;
    setResolving(true);
    setErr(null);
    try {
      await onResolve(d);
      setDescribe('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'resolve failed');
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
    <aside className="w-60 shrink-0 border-r border-border bg-bg flex flex-col">
      <div className="p-3 border-b border-border">
        <div className="text-[11px] uppercase tracking-wider text-texts mb-2 font-medium">
          Skills Palette
          <span className="ml-1.5 normal-case tracking-normal text-texts/80">{skills.length}</span>
        </div>
        <form role="search" autoComplete="off" onSubmit={(e) => e.preventDefault()} className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-texts text-sm pointer-events-none" />
          <input
            type="search"
            name="skill-search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills..."
            className="w-full appearance-none bg-surface border border-border rounded-md pl-8 pr-2 py-1.5 text-sm text-textp placeholder:text-texts outline-none focus:border-accent"
          />
        </form>
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-wider text-texts mb-1 font-medium">Describe a skill</div>
          <textarea
            value={describe}
            onChange={(e) => setDescribe(e.target.value)}
            placeholder="e.g. review my code diff for bugs"
            rows={2}
            className="w-full bg-surface border border-border rounded-md px-2 py-1.5 text-xs text-textp placeholder:text-texts outline-none focus:border-accent resize-none"
          />
          <button
            onClick={submitDescribe}
            disabled={resolving || !describe.trim()}
            className="mt-1 w-full flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-xs font-medium px-2 py-1.5 rounded-md"
          >
            {resolving ? <><i className="ri-loader-4-line animate-spin" /> Resolving</> : <><i className="ri-magic-line" /> Resolve</>}
          </button>
          {err && <div className="mt-1 text-[11px] text-error">{err}</div>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {groupedFiltered.map(({ discipline, skills: items }) => {
          if (items.length === 0) return null;
          const isOpen = open[discipline] ?? groupStartsOpen(discipline);
          return (
            <div key={discipline}>
              <button
                onClick={() => setOpen((o) => ({ ...o, [discipline]: !isOpen }))}
                className="w-full flex items-center justify-between px-2 py-1 text-[11px] uppercase tracking-wider text-texts hover:text-textp"
              >
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[discipline] || categoryColor(discipline) }} />
                  {discipline}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${isOpen ? '' : '-rotate-90'}`} />
              </button>
              {isOpen && (
                <div className="mt-1 space-y-1.5">
                  {items.map((s) => {
                    const viewing = previewSkill?.id === s.id;
                    return (
                    <div
                      key={s.id}
                      className={`group bg-surface hover:bg-surface2 border rounded-lg p-2.5 transition-colors ${viewing ? 'border-accent' : 'border-border hover:border-accent/50'}`}
                    >
                      <div
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('skill/id', s.id);
                          e.dataTransfer.effectAllowed = 'copy';
                          onDragStart(s);
                        }}
                        className="flex items-start gap-2 cursor-grab active:cursor-grabbing"
                      >
                        <div
                          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                          style={{ background: `${(CATEGORY_COLORS[s.category] || categoryColor(s.category))}22`, color: CATEGORY_COLORS[s.category] || categoryColor(s.category) }}
                        >
                          <i className={`${s.icon || 'ri-cpu-line'} text-sm`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-1">
                            <div className="text-[13px] font-semibold text-textp truncate">{s.name}</div>
                            <i className="ri-drag-move-2-line text-texts text-xs opacity-0 group-hover:opacity-100" />
                          </div>
                          <div className="text-[11px] text-texts leading-snug line-clamp-2">{s.description}</div>
                          <div className="text-[10px] text-texts/70 mt-0.5">{ioCardLabel(s)}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-pressed={viewing}
                        onClick={() => onPreviewSkill(viewing ? null : s.id)}
                        className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border ${viewing ? 'text-accent bg-accent/15 border-accent/40' : 'text-texts border-border hover:text-textp hover:border-accent/40'}`}
                      >
                        <i className={`${viewing ? 'ri-eye-off-line' : 'ri-eye-line'} text-xs`} />
                        {viewing ? 'Hide' : 'View'}
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div className="pt-2 border-t border-border">
          <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-texts font-medium">Canvas Objects</div>
          <div className="mt-1 space-y-1.5">
            {TOOLS.map((t) => (
              <div
                key={t.type}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('tool/type', t.type);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                className="group bg-surface hover:bg-surface2 border border-border hover:border-accent/50 rounded-lg p-2.5 cursor-grab active:cursor-grabbing transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${t.wrap}`}>
                    <i className={`${t.icon} text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-textp">{t.name}</div>
                    <div className="text-[11px] text-texts leading-snug">{t.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
    {previewSkill && <SkillPreviewDrawer skill={previewSkill} onClose={() => onPreviewSkill(null)} />}
    </>
  );
}
